/**
 * D-240 / plan §4 A5 — a LEADING-edge throttle, and why it is not the debounce
 * `app.vue` already has.
 *
 * `app.vue.debounce` is trailing-only: it waits out the quiet period and then fires.
 * That is right for a resize beacon and wrong here. The thing being rate-limited is
 * `aria-valuetext` on a range the user may be arrowing, so the FIRST call has to land
 * immediately — a trailing-only 5 s delay means a keyboard user presses Right and
 * hears their new position five seconds later, which is worse than hearing the
 * percentage.
 *
 * So: fire on the leading edge, swallow everything for `wait`, and fire ONCE more at
 * the end of the window if anything was swallowed — the trailing call is what keeps
 * the last announced value honest instead of stranding it at whatever the window
 * happened to open on.
 *
 * 🪤 `.cancel()` is not decoration. A pending trailing timer outlives the component
 * that armed it, which is exactly the defect A4 found in `Utils/Modal` (`release()`
 * defined and never called) — and here it would call back into a scrubber whose
 * `$data` has been torn down. The mixin's `beforeUnmount` calls this, and there is a
 * mutant for it.
 *
 * @param {Function} fn
 * @param {number} wait milliseconds
 * @return {Function} throttled, carrying `.cancel()` and `.flush()`
 */
export default function throttle(fn, wait) {
  let timeout = null;
  let pendingArgs = null;

  const throttled = (...args) => {
    if (timeout) {
      // Inside the window: remember the newest arguments and say nothing.
      pendingArgs = args;
      return;
    }

    fn(...args);

    timeout = setTimeout(() => {
      timeout = null;
      // Only if something was actually swallowed. Firing unconditionally here would
      // double every single call, which is the bug that makes a throttle look like
      // it is not working at all.
      if (pendingArgs) {
        const args2 = pendingArgs;
        pendingArgs = null;
        throttled(...args2);
      }
    }, wait);
  };

  throttled.cancel = () => {
    clearTimeout(timeout);
    timeout = null;
    pendingArgs = null;
  };

  // Used by the user-seek path: a deliberate interaction must be announced now, and
  // must also re-open the window so the announcement is not immediately followed by
  // a throttled one.
  throttled.flush = (...args) => {
    throttled.cancel();
    throttled(...args);
  };

  return throttled;
}
